import React, { useState, useMemo, useRef, useLayoutEffect } from 'react';
import { useCombobox } from 'downshift';
import classNames from 'classnames';
import { Icon } from 'js/components';

function teamToLabel(team) {
  return team ? team.name || String(team.id) : '';
}

export default function RubricPreviewTeamCombobox({
  placeholder = 'Select or search teams…',
  teams = [],
  selectedId,
  onSelect,
  disabled = false,
  id: inputId,
}) {
  const [inputValue, setInputValue] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const userTypedThisSessionRef = useRef(false);
  const cursorRef = useRef(null);
  const [cursor, setCursor] = useState(null);

  const selectedTeam = useMemo(
    () => teams.find((t) => String(t.id) === String(selectedId ?? '')) || null,
    [teams, selectedId]
  );

  const selectedDisplayLabel = useMemo(() => {
    if (selectedTeam) return teamToLabel(selectedTeam);
    return '';
  }, [selectedTeam]);

  const filteredTeams = useMemo(() => {
    const q = inputValue.trim().toLowerCase();
    if (!q) return teams;
    return teams.filter(
      (t) =>
        teamToLabel(t).toLowerCase().includes(q) ||
        String(t.id).toLowerCase().includes(q)
    );
  }, [teams, inputValue]);

  const {
    isOpen,
    getToggleButtonProps,
    getMenuProps,
    getInputProps,
    highlightedIndex,
    getItemProps,
  } = useCombobox({
    items: filteredTeams,
    selectedItem: selectedTeam,
    inputValue: isFocused ? inputValue : selectedDisplayLabel || inputValue,
    itemToString: teamToLabel,
    onInputValueChange: ({ inputValue: next }) => {
      userTypedThisSessionRef.current = true;
      setInputValue(next ?? '');
    },
    onSelectedItemChange: ({ selectedItem }) => {
      if (selectedItem) onSelect(String(selectedItem.id));
    },
    onStateChange: ({ type, selectedItem }) => {
      if (type === useCombobox.stateChangeTypes.ItemClick && selectedItem) {
        onSelect(String(selectedItem.id));
        setInputValue('');
      }
    },
  });

  const downshiftInputProps = getInputProps({
    id: inputId,
    disabled,
    placeholder,
    ref: cursorRef,
  });

  const displayValue =
    isFocused && !inputValue.trim()
      ? selectedDisplayLabel
      : downshiftInputProps.value;

  useLayoutEffect(() => {
    const input = cursorRef.current;
    if (input && cursor != null) {
      input.setSelectionRange(cursor, cursor);
    }
  }, [cursor, displayValue]);

  const inputProps = {
    ...downshiftInputProps,
    value: displayValue,
    onChange: (e) => {
      setCursor(e.target.selectionStart);
      downshiftInputProps.onChange?.(e);
    },
    onFocus: (e) => {
      userTypedThisSessionRef.current = false;
      setIsFocused(true);
      downshiftInputProps.onFocus?.(e);
    },
    onBlur: (e) => {
      setIsFocused(false);
      if (!inputValue.trim() && selectedId && userTypedThisSessionRef.current) {
        onSelect('');
      }
      setInputValue('');
      downshiftInputProps.onBlur?.(e);
    },
  };
  const toggleProps = getToggleButtonProps({ disabled });

  return (
    <div className="combobox">
      <div className="input-group limited-width-lg">
        <input
          placeholder={placeholder}
          className="form-control"
          {...inputProps}
        />
        <div className="input-group-append">
          <span aria-label="toggle menu" type="button" {...toggleProps}>
            {isOpen ? (
              <Icon icon={['far', 'chevron-up']} fixedWidth />
            ) : (
              <Icon icon={['far', 'chevron-down']} fixedWidth />
            )}
          </span>
        </div>
      </div>
      <ul
        {...getMenuProps()}
        className="combobox-items"
        style={!isOpen ? { display: 'none' } : {}}
      >
        {isOpen &&
          filteredTeams.map((item, index) => (
            <li
              className={classNames({
                highlighted: highlightedIndex === index,
                'combobox-item': true,
              })}
              key={`${item.id}-${index}`}
              {...getItemProps({ item, index })}
            >
              <span>{teamToLabel(item)}</span>
            </li>
          ))}
      </ul>
    </div>
  );
}
